import { useEffect, useState } from "react";
import type { SyntheticEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { adminApi } from "../api";
import { Button, Select } from "../components/ui";
import { getApiErrorMessage } from "../utils";
import { ALL_ROLES } from "../constants/roles";

const ROLES = [...ALL_ROLES].reverse();

type TeamOption = { id: string; name: string };

export default function AdminInviteUserPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({ full_name: "", email: "", password: "", role_name: "viewer", team_id: "" });
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    adminApi.listTeams()
      .then((res) => setTeams(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTeams([]));
  }, []);

  function set<K extends keyof typeof form>(field: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function submit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminApi.createUser({ ...form, team_id: form.team_id || null });
      navigate("/admin?tab=Users");
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to create user"));
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6">
      <button
        onClick={() => navigate("/admin?tab=Users")}
        className="mb-6 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft size={15} /> Back to users
      </button>

      <h1 className="mb-6 text-lg font-semibold text-gray-900">Invite user</h1>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Full name</label>
          <input
            required
            value={form.full_name}
            onChange={(e) => set("full_name", e.target.value)}
            placeholder="Jane Smith"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="jane@example.com"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Temporary password</label>
          <input
            required
            type="password"
            value={form.password}
            onChange={(e) => set("password", e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
          <Select value={form.role_name} onChange={(e) => set("role_name", e.target.value)} className="w-full">
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Team <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <Select value={form.team_id} onChange={(e) => set("team_id", e.target.value)} className="w-full">
            <option value="">No team</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex items-center gap-3 pt-2">
          <Button type="submit" disabled={loading}>
            {loading ? "Creating…" : "Create user"}
          </Button>
          <Button variant="secondary" type="button" onClick={() => navigate("/admin?tab=Users")}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
